import { latest as LatestVDETypes } from '@libs/waymark-video/video-descriptor-types';
import { matchObservableObjectChange, ObservableObjectChange } from '@libs/util-ts';

import { BaseLayer } from './BaseLayer';
import { getFormattedColor, hexStringToRGBArray, rgbArrayToHexString } from '../utils/colors';
import { FontAsset } from '../assets';

export class TextLayer extends BaseLayer<
  LatestVDETypes.TextLayer,
  {
    'change:textContent': undefined;
    'change:fillColor': undefined;
    'change:strokeColor': undefined;
    'change:font': undefined;
    'change:fontSize': undefined;
    'change:lineHeight': undefined;
    'change:resizingStrategy': undefined;
    'change:visibility': void;
    'change:fillEffectColor': undefined;
    change: void;
    'change:ignoredReferences': void;
    removed: undefined;
  }
> {
  static layerType: LatestVDETypes.TextLayer['ty'] = LatestVDETypes.LayerType.Text;

  onLayerDataChange(change: ObservableObjectChange): boolean {
    let didChange = false;

    if (super.onLayerDataChange(change)) {
      didChange = true;
    }

    if (matchObservableObjectChange(change, 'refId')) {
      // refId is the text layer's font asset ID
      this.dispatchEvent('change:font');
      didChange = true;
    }

    if (matchObservableObjectChange(change, 't.d.k.0.s.t')) {
      // text content is on every keyframe at s.t, so we can just check the first one
      this.dispatchEvent('change:textContent');
      didChange = true;
    }

    if (matchObservableObjectChange(change, 't.d.k.0.s.fc')) {
      // fill color is on every keyframe at s.fc
      this.dispatchEvent('change:fillColor');
      didChange = true;
    }

    if (matchObservableObjectChange(change, 't.d.k.0.s.sc')) {
      // stroke color is on every keyframe at s.sc
      this.dispatchEvent('change:strokeColor');
      didChange = true;
    }

    if (matchObservableObjectChange(change, 't.d.k.0.s.s')) {
      // font size is on every keyframe at s.s
      this.dispatchEvent('change:fontSize');
      didChange = true;
    }

    if (matchObservableObjectChange(change, 't.d.k.0.s.lh')) {
      // line height is on every keyframe at s.lh
      this.dispatchEvent('change:lineHeight');
      didChange = true;
    }

    if (matchObservableObjectChange(change, 'meta.textOptions.resizingStrategy')) {
      // resizing strategy is on the meta object
      this.dispatchEvent('change:resizingStrategy');
      didChange = true;
    }

    return didChange;
  }

  /** ---- TEXT CONTENT ---- */

  /**
   * Get the current text content of this layer.
   */
  getText() {
    return this.rawLayerData.t.d.k[0].s.t;
  }

  /**
   * @emits contentChange
   * Performs the actual text content update. Necessary because setText and onTextContentReferenceChange
   * need to have slightly different behavior.
   */
  private updateTextContent(text: string) {
    const keyframes = this.rawLayerData.t.d.k;
    // Change text content for all keyframes
    for (const keyframe of keyframes) {
      keyframe.s.t = text;
    }
  }

  /**
   * Updates the text content of this layer.
   * If shouldUpdateReference is true, the reference will be updated and the change will be
   * propagated to all layers using the same reference.
   */
  setText(text: string, shouldOverrideReference = false) {
    if (!this.getCanEditFieldType('content')) {
      throw new Error(`${this.toString()}: Text layer content is not configured to be editable.`);
    }

    if (shouldOverrideReference) {
      this.updateIgnoredReferences({
        textContent: true,
      });
    }

    if (this.textContentReference && !this.getIsReferenceIgnored('textContent')) {
      // Update the reference instead of directly just updating this layer;
      // that will trigger the reference's change:value event, which will then come back around
      // to update this layer with any other layers pointing to the same reference
      this.textContentReference.setText(text);
    } else {
      // Update the text content directly if we're not updating a reference
      this.updateTextContent(text);
    }
  }

  /**
   * Event listener for when the text content reference changes.
   * This is an arrow function to avoid weirdness with `this` when this gets passed to `addEventListener`.
   */
  onTextContentReferenceChange(): void {
    if (!this.textContentReference || this.getIsReferenceIgnored('textContent')) {
      return;
    }

    const newTextContent = this.textContentReference.getText();
    this.updateTextContent(newTextContent);
  }

  /** ---- FILL COLOR ---- */

  /**
   * Gets the current fill color of this text layer.
   *
   * @param format - The format to return the color in. 'hex' returns a hex code string, 'rgb' returns a tuple of 0-1 RGB channel values.
   */
  getFillColor<TColorFormat extends 'hex' | 'rgb' = 'hex'>(format = 'hex' as TColorFormat) {
    const rgbFillColor = this.rawLayerData.t.d.k[0].s.fc;
    return rgbFillColor
      ? getFormattedColor(rgbFillColor as [number, number, number], format)
      : null;
  }

  /**
   * @emits contentChange
   * Performs the actual text content update; used by setFillColor and onFillColorReferenceChange listener
   */
  private updateFillColor(newColor: [number, number, number]) {
    // Change fill color for all keyframes
    for (const keyframe of this.rawLayerData.t.d.k) {
      keyframe.s.fc = newColor;
    }
  }

  /**
   * Updates the fill color of this layer.
   * If shouldOverrideReference is true, the layer will be updated to ignore its fill color reference
   * and the fill color will be set directly.
   */
  setFillColor(newColor: string | [number, number, number], shouldOverrideReference = false) {
    if (!this.getCanEditFieldType('fillColor')) {
      throw new Error(
        `${this.toString()}: Text layer fill color is not configured to be editable.`,
      );
    }

    if (shouldOverrideReference) {
      this.updateIgnoredReferences({
        fillColor: true,
      });
    }

    if (this.fillColorReference && !this.getIsReferenceIgnored('fillColor')) {
      // Ensure the color is a hex string because that's how we store color reference values
      this.fillColorReference.setColor(
        Array.isArray(newColor) ? rgbArrayToHexString(newColor) : newColor,
      );
    } else {
      // Ensure the color is an RGB array
      this.updateFillColor(Array.isArray(newColor) ? newColor : hexStringToRGBArray(newColor));
    }
  }

  /**
   * Listener updates the fill color when the fill color reference changes, unless the layer is ignoring the reference.
   * This is an arrow function to avoid weirdness with `this` when this gets passed to `addEventListener`.
   */
  onFillColorReferenceChange(): void {
    if (!this.fillColorReference || this.getIsReferenceIgnored('fillColor')) {
      return;
    }

    const newFillColorHex = this.fillColorReference.getColor();
    this.updateFillColor(hexStringToRGBArray(newFillColorHex));
  }

  /** ---- STROKE COLOR ---- */

  /**
   * Gets the current stroke color of this text layer.
   *
   * @param format - The format to return the color in. 'hex' returns a hex code string, 'rgb' returns a tuple of 0-1 RGB channel values.
   */
  getStrokeColor<TColorFormat extends 'hex' | 'rgb' = 'hex'>(format = 'hex' as TColorFormat) {
    const rgbFillColor = this.rawLayerData.t.d.k[0].s.sc;
    return rgbFillColor
      ? getFormattedColor(rgbFillColor as [number, number, number], format)
      : null;
  }

  /**
   * @emits contentChange
   * Performs the actual stroke color update; used by setStrokeColor and onStrokeColorReferenceChange listener
   */
  private updateStrokeColor(newColor: [number, number, number]) {
    // Change stroke color for all keyframes
    for (const keyframe of this.rawLayerData.t.d.k) {
      keyframe.s.sc = newColor;
    }
  }

  /**
   * Updates the stroke color of this layer, or the layer's color reference if it has one.
   */
  setStrokeColor(newColor: string | [number, number, number], shouldOverrideReference = false) {
    if (!this.getCanEditFieldType('strokeColor')) {
      throw new Error(
        `${this.toString()}: Text layer stroke color is not configured to be editable.`,
      );
    }

    if (shouldOverrideReference) {
      this.updateIgnoredReferences({
        strokeColor: true,
      });
    }

    if (this.strokeColorReference && !this.getIsReferenceIgnored('strokeColor')) {
      this.strokeColorReference.setColor(
        Array.isArray(newColor) ? rgbArrayToHexString(newColor) : newColor,
      );
    } else {
      this.updateStrokeColor(Array.isArray(newColor) ? newColor : hexStringToRGBArray(newColor));
    }
  }

  /**
   * Listener updates the stroke color when the stroke color reference changes, unless the layer is ignoring the reference.
   * This is an arrow function to avoid weirdness with `this` when this gets passed to `addEventListener`.
   */
  onStrokeColorReferenceChange(): void {
    if (!this.strokeColorReference || this.getIsReferenceIgnored('strokeColor')) {
      return;
    }

    const newStrokeColorHex = this.strokeColorReference.getColor();
    this.updateStrokeColor(hexStringToRGBArray(newStrokeColorHex));
  }

  /** ---- FONT ---- */

  getFontAssetID() {
    return this.rawLayerData.refId;
  }

  /**
   * Gets the font asset which this layer is currently using.
   */
  getFontAsset(): FontAsset {
    const fontAssetID = this.getFontAssetID();
    if (!fontAssetID) {
      throw new Error(`${this.toString()}: Cannot get font asset for text layer missing refId`);
    }

    const fontAsset = this.videoDescriptor.getAssetByID(fontAssetID);
    if (!(fontAsset instanceof FontAsset)) {
      throw new Error(`${this.toString()}: Font asset with ID ${fontAssetID} not found`);
    }

    return fontAsset;
  }

  /**
   * Updates the font asset which this layer is using.
   * @emits contentChange
   */
  private updateFontAssetRefID(fontAssetID: string) {
    this.rawLayerData.refId = fontAssetID;
    // Change font ID for all keyframes
    for (const keyframe of this.rawLayerData.t.d.k) {
      keyframe.s.f = fontAssetID;
    }
  }

  setFontAsset(fontAsset: FontAsset, shouldOverrideReference = false) {
    if (!this.getCanEditFieldType('font')) {
      throw new Error(`${this.toString()}: Text layer font is not configured to be editable.`);
    }

    const oldAsset = this.getFontAsset();

    // Ensure the font asset is in the video descriptor
    this.videoDescriptor.addAsset(fontAsset);

    if (shouldOverrideReference) {
      this.updateIgnoredReferences({
        font: true,
      });
    }

    if (this.fontReference && !this.getIsReferenceIgnored('font')) {
      // Update the reference instead of directly just updating this layer;
      this.fontReference.setFontAsset(fontAsset);
    } else {
      // Update the font asset directly if we're not updating a reference
      this.updateFontAssetRefID(fontAsset.getID());

      // Clean up the old asset if it's no longer in use
      this.videoDescriptor.removeAsset(oldAsset);
    }
  }

  /*
   * Listener updates the font asset when the font reference changes, unless the layer is ignoring the reference.
   * This is an arrow function to avoid weirdness with `this` when this gets passed to `addEventListener`.
   */
  onFontReferenceChange(): void {
    if (!this.fontReference || this.getIsReferenceIgnored('font')) {
      return;
    }

    const newFontAsset = this.fontReference.getFontAsset();
    this.updateFontAssetRefID(newFontAsset.getID());
  }

  /**
   * Get the font size of the text layer.
   */
  getFontSize(): number {
    return this.rawLayerData.t.d.k[0].s.s;
  }

  /**
   * Get the line height of the text layer.
   */
  getLineHeight(): number {
    return this.rawLayerData.t.d.k[0].s.lh;
  }

  /**
   * Get the suggested line height for a given font size, based on the layer's
   * current line height and font size.
   */
  getSuggestedLineHeightForFontSize(fontSize: number): number {
    const currentLineHeight = this.getLineHeight();
    const currentFontSize = this.getFontSize();
    return Math.max(fontSize * (currentLineHeight / currentFontSize), 1);
  }

  /**
   * Updates the font size of the text layer.
   * @param newFontSize - The new font size to set.
   * @param shouldAutoUpdateLineHeight - Whether we should also update the line height based on the new font size.
   */
  setFontSize(newFontSize: number, shouldAutoUpdateLineHeight = true): void {
    if (
      this.rawLayerData.meta.textOptions?.resizingStrategy ===
      LatestVDETypes.TextResizingStrategy.StepAndBreakWords
    ) {
      throw new Error(
        `${this.toString()}: Cannot set font size on a text layer with the StepAndBreakWords resizing strategy, as its font size dynamically changes based on available space.`,
      );
    }

    const newLineHeight = shouldAutoUpdateLineHeight
      ? this.getSuggestedLineHeightForFontSize(newFontSize)
      : null;

    for (const keyframe of this.rawLayerData.t.d.k) {
      // Ensure we can't set a negative font size
      keyframe.s.s = Math.max(newFontSize, 1);
      if (newLineHeight !== null) {
        keyframe.s.lh = newLineHeight;
      }
    }
  }

  /**
   * Updates the line height of the text layer.
   */
  setLineHeight(newLineHeight: number) {
    for (const keyframe of this.rawLayerData.t.d.k) {
      // Ensure we can't set a negative line height
      keyframe.s.lh = Math.max(newLineHeight, 0);
    }
  }

  /**
   * Get the current resizing strategy for the text layer.
   */
  getResizingStrategy(): `${LatestVDETypes.TextResizingStrategy}` {
    const resizingStrategy = this.rawLayerData.meta.textOptions?.resizingStrategy;

    if (!resizingStrategy) {
      return LatestVDETypes.TextResizingStrategy.Default;
    }

    // resizingStrategy may be a tuple where the first item is the TextResizingStrategy and the
    // second item is a TextResizingStrategyStepDirection
    if (Array.isArray(resizingStrategy)) {
      return resizingStrategy[0];
    }

    return resizingStrategy;
  }

  /**
   * Set the resizing strategy for the text layer.
   */
  setResizingStrategy(resizingStrategy: `${LatestVDETypes.TextResizingStrategy}`) {
    this.rawLayerData.meta.textOptions ??= {};
    const textOptions = this.rawLayerData.meta.textOptions;

    if (Array.isArray(textOptions.resizingStrategy)) {
      // resizingStrategy may be a tuple where the first item is the TextResizingStrategy and the
      // second item is a TextResizingStrategyStepDirection
      textOptions.resizingStrategy[0] = resizingStrategy;
    } else {
      textOptions.resizingStrategy = resizingStrategy;
    }
  }

  cleanUp(): void {
    super.cleanUp();
    // Clean up the font asset
    try {
      const fontAsset = this.getFontAsset();
      this.videoDescriptor.removeAsset(fontAsset);
    } catch {
      // Swallow errors if the asset was not found; we don't care if we're cleaning up the layer
    }
  }
}
